import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation, useRouter } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { SafeArea, type SafeAreaInsets } from 'capacitor-plugin-safe-area'
import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
import { App } from '@capacitor/app'
import { queryClient, sessionQueryOptions } from '@/shared/lib/tanstack'
import { ROUTES } from '@/shared/constants/routes'
import { AUTH_TOKEN_KEY } from '@/shared/constants/auth'
import { SESSION_QUERY_KEY } from '@/shared/constants/cache'
import { CAPACITOR_EVENT } from '@/shared/constants/capacitor'
import { PRODUCT } from '@/shared/constants/product'
import {
  ANIMATION_DURATION_MS,
  MOUNT_DELAY_MS,
} from '@/shared/constants/timings'
import { getLoginRedirectSearch } from '@/pages/home/page.home.hook'
import { sendEvent, identifyUser } from '@/shared/hooks/use-analytics'
import { useUser } from '@/shared/hooks/use-user'
import { usePreloadPaymentIframe } from '@/shared/hooks/use-payment-iframe'
import { useIframeVisibility } from '@/shared/context/iframe-visibility-context'
import { useAppVersionFlag } from '@/shared/hooks/use-app-version-flag'
import { useHideHeader } from '@/shared/hooks/use-hide-header'
import { SplashScreen } from '@capacitor/splash-screen'
import i18n from '@/shared/lib/i18n'
import { getSecureItem } from '@/shared/lib/storage'
import { LANGUAGE_STORAGE_KEY } from '@/shared/constants/language'

const AUTH_ROUTES = [ROUTES.LOGIN, ROUTES.VERIFY_CODE] as const
const PUBLIC_ROUTES = [...AUTH_ROUTES, ROUTES.DESIGN_SYSTEM] as const

function applyInsets({ insets }: SafeAreaInsets) {
  for (const [key, value] of Object.entries(insets) as [string, number][]) {
    if (value === 0) continue
    document.documentElement.style.setProperty(
      `--safe-area-inset-${key}`,
      `${value}px`,
    )
  }
}

export function useRootLayout() {
  useUser()

  useEffect(() => {
    document.getElementById('app-loading')?.remove()
    getSecureItem(LANGUAGE_STORAGE_KEY).then((storedLang) => {
      if (storedLang) i18n.changeLanguage(storedLang)
      SplashScreen.hide({ fadeOutDuration: ANIMATION_DURATION_MS }).catch(
        () => {},
      )
    })
  }, [])

  const navigate = useNavigate()
  const location = useLocation()
  const router = useRouter()
  const { isPayInOpen, closeHandlerRef } = useIframeVisibility()
  const hideHeader = useHideHeader()
  const isAuthPage = AUTH_ROUTES.some((r) => location.pathname.startsWith(r))
  usePreloadPaymentIframe(PRODUCT.PORTAL, !isAuthPage)
  const { status: appVersionStatus, payload: appVersionPayload } =
    useAppVersionFlag()

  const email = (location.search as { email?: string }).email
  sendEvent('is_prefilled', { is_prefilled: !!email })
  if (email) identifyUser({ email })

  const [safeAreaTop, setSafeAreaTop] = useState(0)
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  const [warnDismissed, setWarnDismissed] = useState(false)

  const isPayInOpenRef = useRef(isPayInOpen)
  useEffect(() => {
    isPayInOpenRef.current = isPayInOpen
  }, [isPayInOpen])

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
    }

    if (!Capacitor.isNativePlatform()) return

    document.documentElement.classList.add('native')
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    document.body.style.height = '100%'

    SafeArea.getSafeAreaInsets().then(({ insets }) => {
      applyInsets({ insets })
      setSafeAreaTop(insets.top)
    })

    let safeAreaHandle:
      | Awaited<ReturnType<typeof SafeArea.addListener>>
      | undefined = undefined

    SafeArea.addListener(CAPACITOR_EVENT.SAFE_AREA_CHANGED, (data) => {
      applyInsets(data)
      setSafeAreaTop(data.insets.top)
    }).then((handle) => {
      safeAreaHandle = handle
    })

    let backButtonHandle:
      | Awaited<ReturnType<typeof App.addListener>>
      | undefined = undefined

    App.addListener('backButton', ({ canGoBack }) => {
      if (isPayInOpenRef.current) {
        closeHandlerRef.current?.()
        return
      }
      if (canGoBack) {
        router.history.back()
        return
      }

      const { pathname } = router.state.location
      const isAuthRoute = AUTH_ROUTES.some((r) => pathname.startsWith(r))
      if (pathname !== ROUTES.HOME && !isAuthRoute) {
        navigate({ to: ROUTES.HOME, replace: true })
      } else {
        App.exitApp()
      }
    }).then((handle) => {
      backButtonHandle = handle
    })

    let appUrlOpenHandle:
      | Awaited<ReturnType<typeof App.addListener>>
      | undefined = undefined

    App.addListener('appUrlOpen', (event) => {
      const url = new URL(event.url)
      navigate({ to: url.pathname + url.search })
    }).then((handle) => {
      appUrlOpenHandle = handle
    })

    let appStateHandle:
      | Awaited<ReturnType<typeof App.addListener>>
      | undefined = undefined

    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        // Re-invalidate session when app comes back to foreground
        router.invalidate()
        // Re-show warn modal every time app comes to foreground
        setWarnDismissed(false)
      }
    }).then((handle) => {
      appStateHandle = handle
    })

    let keyboardShowHandle:
      | Awaited<ReturnType<typeof Keyboard.addListener>>
      | undefined = undefined
    let keyboardHideHandle:
      | Awaited<ReturnType<typeof Keyboard.addListener>>
      | undefined = undefined

    Keyboard.addListener(
      CAPACITOR_EVENT.KEYBOARD_WILL_SHOW,
      ({ keyboardHeight: h }) => {
        setKeyboardHeight(h)
      },
    ).then((handle) => {
      keyboardShowHandle = handle
    })

    Keyboard.addListener(CAPACITOR_EVENT.KEYBOARD_WILL_HIDE, () => {
      setKeyboardHeight(0)
    }).then((handle) => {
      keyboardHideHandle = handle
    })

    return () => {
      safeAreaHandle?.remove()
      backButtonHandle?.remove()
      appUrlOpenHandle?.remove()
      appStateHandle?.remove()
      keyboardShowHandle?.remove()
      keyboardHideHandle?.remove()
    }
  }, [navigate])

  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)

    if (isPayInOpen) {
      setMounted(true)
      timerRef.current = setTimeout(() => setVisible(true), MOUNT_DELAY_MS)
    } else {
      setMounted(false)
      setVisible(false)
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [isPayInOpen])

  const handleClose = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setMounted(false)
    setVisible(false)
    closeHandlerRef.current?.()
  }

  return {
    isAuthPage,
    hideHeader,
    mounted,
    visible,
    handleClose,
    safeAreaTop,
    keyboardHeight,
    appVersionStatus,
    appVersionPayload,
    warnDismissed,
    setWarnDismissed,
  }
}

export function useNotFound() {
  const { pathname } = useLocation()
  const cachedSession = queryClient.getQueryData<{ user?: object }>(
    SESSION_QUERY_KEY,
  )
  const hasToken: boolean =
    !!cachedSession?.user || !!localStorage.getItem(AUTH_TOKEN_KEY)
  const { data: session, isLoading } = useQuery({
    ...sessionQueryOptions,
    enabled: hasToken,
  })
  const navigate: ReturnType<typeof useNavigate> = useNavigate()

  useEffect(() => {
    const isPublicPage = PUBLIC_ROUTES.some((r) => pathname.startsWith(r))
    if (!isPublicPage && (!hasToken || (!isLoading && !session))) {
      navigate({
        to: ROUTES.LOGIN,
        search: getLoginRedirectSearch({
          pathname: window.location.pathname,
          search: Object.fromEntries(
            new URLSearchParams(window.location.search),
          ),
        }),
      })
    }
  }, [hasToken, session, isLoading, navigate, pathname])

  return { isAuthenticated: !!session, isLoading: hasToken && isLoading }
}
